import { useState, useMemo, useCallback, useEffect } from 'react'
import { useTheme } from '@homebotapp/hb-react-component-catalog'
import { ListingStatusEnum } from '../../../../../../types/listingStatus'
import { getMarkerColors, MarkerState, ThemeColors, clearMarkerColorsCache } from '../markerColors'

export interface UseMarkerColorsProps {
  status: ListingStatusEnum
  isViewed: boolean
  isSelected: boolean
  isActive?: boolean
}

export const useMarkerColors = ({ status, isViewed, isSelected, isActive = false }: UseMarkerColorsProps) => {
  const { theme } = useTheme()
  const [isHovered, setIsHovered] = useState(false)

  // Clear cache when theme changes
  useEffect(() => {
    clearMarkerColorsCache()
  }, [theme.isLightThemeType])

  // Memoize theme colors to prevent unnecessary recalculations
  const themeColors: ThemeColors = useMemo(
    () => ({
      isLightTheme: theme.isLightThemeType
    }),
    [theme.isLightThemeType]
  )

  // Memoize marker state to prevent unnecessary recalculations
  const markerState: MarkerState = useMemo(
    () => ({
      status,
      isViewed,
      isHovered,
      isSelected,
      isActive
    }),
    [status, isViewed, isHovered, isSelected, isActive]
  )

  // Memoize colors calculation - only recalculate when dependencies change
  const colors = useMemo(() => {
    return getMarkerColors(markerState, themeColors)
  }, [markerState, themeColors])

  // Memoize event handlers to prevent unnecessary re-renders
  const onMouseEnter = useCallback(() => setIsHovered(true), [])
  const onMouseLeave = useCallback(() => setIsHovered(false), [])

  return {
    colors,
    isHovered,
    isActive,
    setIsHovered,
    onMouseEnter,
    onMouseLeave
  }
}
